import { type FC, type ReactNode, useRef, useState } from "react";

import { ApolloClient, ApolloLink, ApolloProvider, type ServerParseError } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { type GraphQLError } from "graphql";

import { useNetworkMetrics } from "../../analytics/hooks/useNetworkMetrics";
import { NetworkMetricsNames } from "../../analytics/network-metrics";
import { GroupsRepositoryV2 } from "../../camkit/groups/model/GroupsRespositoryV2";
import { AffectedGroupsRepository, LensSourcesRepository } from "../../camkit/lens-source/api/cache";
import { LensesRepositoryV2 } from "../../camkit/lenses/models/LensesRepositoryV2";
import { OrganizationsRepository } from "../../camkit/organizations/model/OrganizationsRepository";
import { useAppGlobalRoutes } from "../../core/routes/hooks/useAppGlobalRoutes";
import { useSnapSSOAuthService } from "../../core/sso-auth";
import { PageContentSpinner } from "../../layout/components/PageContentSpinner";
import { GRPC_ERROR_CODES, useNotificationApi } from "../../notifications";
import { isProd } from "../../shared/constants";

import { authLink } from "./AuthLink";
import { cache } from "./Cache";
import { HTTP_STATUS_CODES, createBaseLinks, hasResponse } from "./createBaseLinks";
import { createTransientRetryLink } from "./createTransientRetryLink";

const hasException = (
    extensions: GraphQLError["extensions"],
): extensions is GraphQLError["extensions"] & { exception: { code: number } } => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Guarded access
    return typeof extensions["exception"] === "object" && typeof extensions["exception"].code === "number";
};

export const AppApolloProvider: FC<{ children?: ReactNode }> = ({ children }) => {
    const notificationApi = useNotificationApi();
    const authService = useSnapSSOAuthService();
    const { goToAccessDenied } = useAppGlobalRoutes();
    const metrics = useNetworkMetrics();

    const handlers = useRef({ notificationApi, authService, goToAccessDenied });
    handlers.current = { notificationApi, authService, goToAccessDenied };
    const [client] = useState(() => {
        const { uploadLink, timeoutLink, networkMetricsLink } = createBaseLinks(metrics);

        const errorLink = new ErrorLink((error) => {
            console.error(error);

            const { networkError, graphQLErrors, operation } = error;

            const context = operation.getContext();
            const operationName = operation.operationName;

            const responseStatusCode = hasResponse(context) ? context.response.status : 0;

            if (graphQLErrors) {
                for (const gqlError of graphQLErrors) {
                    const { path, extensions, message } = gqlError;

                    // Transform lens lock errors to user-friendly messages
                    const lensLockErrorRegex = /lens.*is currently locked/i;
                    if (lensLockErrorRegex.test(message)) {
                        gqlError.message =
                            "You just made a change. Please wait for the change to be processed before making additional changes.";
                    }

                    const errorCode = hasException(extensions) ? extensions.exception.code : undefined;

                    // expected Auth-Z error
                    const isExpected403Error =
                        operationName === "getLensDestinations" && errorCode === GRPC_ERROR_CODES.PERMISSION_DENIED;

                    if (!isExpected403Error) {
                        metrics.reportMetric({
                            metricName: NetworkMetricsNames.APOLLO_GRAPHQL_ERROR,
                            label: operationName,
                            statusCode: errorCode ?? responseStatusCode,
                        });

                        handlers.current.notificationApi.sendError({
                            path: path?.[0] ?? "Unknown",
                            errorCode: errorCode ?? responseStatusCode,
                            message,
                        });
                    }
                }
            }

            if (networkError) {
                // Check the timeout error object for the status code for event of a TimeoutError
                const networkErrorStatusCode =
                    responseStatusCode || (networkError as { statusCode: number }).statusCode;

                metrics.reportMetric({
                    metricName: NetworkMetricsNames.APOLLO_NETWORK_ERROR,
                    label: operationName,
                    statusCode: networkErrorStatusCode,
                });
                switch (networkErrorStatusCode) {
                    case HTTP_STATUS_CODES.FORBIDDEN:
                        handlers.current.goToAccessDenied();
                }
            }
        });

        const retryAuthLink = new RetryLink({
            attempts: {
                max: 5,
                retryIf: ({ response }: Partial<ServerParseError>) => {
                    if (response?.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
                        return handlers.current.authService.refreshToken().then(() => true);
                    }
                    return false;
                },
            },
        });

        const client = new ApolloClient({
            cache,
            link: ApolloLink.from([
                retryAuthLink,
                networkMetricsLink,
                errorLink,
                createTransientRetryLink(),
                timeoutLink,
                authLink,
                uploadLink,
            ]),
            connectToDevTools: !isProd(),
        });

        // Re-seed defaults on organization switches without replacing the active client.
        client.onResetStore(() => {
            OrganizationsRepository.initDefaults(cache);
            GroupsRepositoryV2.initDefaults(cache);
            LensesRepositoryV2.initDefaults(cache);
            LensSourcesRepository.initDefaults(cache);
            AffectedGroupsRepository.initDefaults(cache);
            return Promise.resolve();
        });

        return client;
    });
    if (!notificationApi.initialized) return <PageContentSpinner />;

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
